import type { DashboardElement } from './vi-dashboard';
import type { IPanelDimensions } from './vi-dashboard-utility';

/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/ban-ts-comment */
import { AsLitHIC, BehaviorElement, HIP } from '@eab-eip/hi-component/hi-component';
import { stylesTheme } from '@eab-eip/hi-component/hi-component-theme';
import { LitElement, css, html, PropertyValues } from 'lit-element';

import dom from '@eab-pip/pi-dom';
import f1 from '@eab-pip/pi-f1';

import './vi-panel-toolbar';

/**
 * the panel information needed to save a dashboard
 */
export interface IPanelLayoutState extends IPanelDimensions {
  id: string;
}

export const styles = css`
  :host {
    --vi-panel--text-color: var(--hi-clr--text);
    --vi-panel--background: var(--hi-clr--white);
    --vi-panel--border-color: var(--hi-clr--gray-5);
    --vi-panel--toolbar--background: var(--hi-clr--gray-1);
    --vi-panel--gap: calc(var(--hi-tile--padding) / 2);
    display: block;
  }

  #panelBox {
    border-radius: var(--hi-tile--border-radius);
    box-shadow: var(--hi-tile--shadow);
    border: var(--hi-tile--border);
    background: var(--hi-clr--white);
    margin: var(--vi-panel--gap);
  }

  #headerBox {
    padding: var(--vi-panel--gap) !important;
  }

  #mainBox {
    position: relative !important;
  }

  :host([allow-overflow]) #mainBox {
    overflow: auto;
  }
`;

/**
 * @fires remove-ended when the panel is removed
 * @fires refresh-ended when the panel is updated
 *
 * @cssprop allow-overflow - allow the main content to overflow
 */
export class PanelElement extends BehaviorElement(AsLitHIC(LitElement)) {
  static get is() {
    return 'vi-panel';
  }

  static get styles() {
    return [stylesTheme, styles];
  }

  /**
   * allow the main content area to have a scrollbar
   * @attr allow-overflow
   */
  allowOverflow = false;
  /**
   * add a remove icon to the toolbar
   * @attr allow-remove
   */
  allowRemove = false;
  /**
   * row : x axis of the panel
   * it accepts string value
   */
  row = 0;
  /**
   * column : y axis of the panel
   * it accepts string value
   */
  column = 0;
  /**
   * width : width of the panel
   * it accepts string value
   */
  width = 1;
  /**
   * height : height of the panel
   * it accepts string value
   * we default the value to 6
   *
   * @note do not change this value
   */
  height = 6;
  /**
   * panelHeaderHeight : height of the panel header
   * it accepts string value
   * we default the value to 80px
   *
   * @note do not change this value
   */
  panelHeaderHeight = 80;

  /**
   *
   */
  input: Record<string, unknown>;
  /**
   * grid stack instance
   */
  grid: any;

  static get properties() {
    return {
      id: {
        type: String
      },
      row: {
        type: Number,
        reflect: true
      },
      column: {
        type: Number,
        reflect: true
      },
      width: {
        type: Number,
        reflect: true
      },
      height: {
        type: Number,
        reflect: true
      },
      allowRemove: {
        type: Boolean,
        attribute: 'allow-remove'
      },
      allowOverflow: {
        type: Boolean,
        attribute: 'allow-overflow',
        reflect: true
      },
      input: {
        type: Object
      },
      panelHeaderHeight: {
        type: Number,
        attribute: 'panel-header-height'
      }
    };
  }

  static get api() {
    return ['remove', 'refresh'];
  }

  connectedCallback() {
    super.connectedCallback();

    this.className = 'grid-stack-item';
    this.refresh();
  }

  render() {
    return html`
      <div id="panelBox">
        <slot name="toolbar"></slot>
        <div id="headerBox">
          <slot name="header"></slot>
        </div>
        <section id="mainBox">
          <slot id="items" @slotchange="${this.__slot__onSlotChange}"></slot>
        </section>
      </div>
    `;
  }

  updated(props: Map<string, unknown>) {
    if (props.has('row') || props.has('column') || props.has('width') || props.has('height')) {
      this.refresh();
    }
  }

  firstUpdated(props: PropertyValues) {
    super.firstUpdated(props);

    this.__computePanelContentHeight();
  }

  /**
   *  returns panel's state
   */
  saveLayoutState(): IPanelLayoutState {
    return {
      height: this.height,
      column: this.column,
      width: this.width,
      row: this.row,
      id: this.id
    };
  }

  /**
   *  loads panels state and updates its properties
   */
  loadLayoutState(state: IPanelLayoutState) {
    this.column = state.column;
    this.height = state.height;
    this.width = state.width;
    this.row = state.row;
    this.id = state.id;

    this.refresh();
  }

  /**
   * Read the panel dimensions and update the panel accordingly
   */
  refresh() {
    const parentEl = this.parentElement as DashboardElement;
    if (parentEl) {
      parentEl.reflectPanel(this);
    }
  }

  /*
   * When called, it will dispatch an event a value of the panel being removed.
   * To use, just have `allow-remove` , it will add close button which calls this method.
   */
  remove() {
    const value = {
      panelState: this.saveLayoutState()
    };

    const parentEl = this.parentElement as DashboardElement;
    if (parentEl) {
      parentEl._dispatchProcess('remove', {
        detail: {
          value
        }
      });
    }

    return value;
  }

  /**
   *  calculate and update panel header and panel content height
   */
  private __computePanelContentHeight() {
    const panelHeaderEl = this.shadowRoot?.querySelector('#headerBox') as HTMLElement;
    const panelContentEl = this.shadowRoot?.querySelector('#mainBox') as HTMLElement;

    // computatation explanation
    // cellHeight of gridstack = 100
    // panelHeaderHeight = 80
    // panelToolbarHeight = 44
    // padding of the header = 12 (top & bottom padding)
    //
    const totalHeight = this.height * 100;
    const panelToolbarHeight = 44; // --hi-el--height-lrg : var(--hi-px-44)
    const totalPaddingOfHeader = 2 * 6; // top & bottom padding
    const panelContentElHeight = totalHeight - this.panelHeaderHeight - panelToolbarHeight - totalPaddingOfHeader;

    if (panelHeaderEl) {
      panelHeaderEl.style.height = `${this.panelHeaderHeight}px`;
    }

    if (panelContentEl) {
      panelContentEl.style.height = `${panelContentElHeight}px`;
    }
  }

  /**
   *  finds panel's slot children and adds gridstack class
   */
  __slot__onSlotChange() {
    const itemsSlotsEl = this.shadowRoot?.querySelector('#items') as HTMLSlotElement;
    const slotChildren = f1.to
      .array(
        itemsSlotsEl.assignedNodes({
          flatten: false
        })
      )
      .filter(dom.is.node) as HTMLElement[];

    const panelContentEl = slotChildren[0];
    if (panelContentEl) {
      panelContentEl.classList.add('grid-stack-item-content');
    }
  }
}

export default HIP.factory.element(PanelElement);
